import { Link, useLocation } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Logo } from "../components/Brand";

const CONTACT = "privacy@zunii.app";
const UPDATED = "Draft — review with a lawyer before publishing";

const DOCS = {
  privacy: {
    title: "Privacy Policy",
    sections: [
      ["Who we are", "Zunii is a parent-supervised creative video app for children. A parent or legal guardian (18+) creates and controls every family account."],
      ["What we collect", "Parent: name, email, hashed password, consent record, device push token (if push is enabled). Child profiles: a nickname, age, avatar, color and a hashed 4-digit PIN. Content: short videos (max 10 seconds), titles, descriptions, comments, cheers, reports and blocks. Usage: daily minutes used and an activity log visible to the parent."],
      ["What we never collect from children", "No real names, email addresses, phone numbers, precise location, contacts, photos outside of what they choose to record, or advertising identifiers. Children cannot message each other privately."],
      ["How we use it", "Only to run Zunii: show approved videos, enforce parent controls, screen time and safety checks. No advertising, no selling or renting data, no behavioral profiling of children."],
      ["Automated safety checks", "Titles, comments and still frames from each video are checked by an AI safety service (Anthropic Claude via our provider) before a parent sees them. Content is processed only for moderation."],
      ["Parent approval", "Nothing a child creates becomes visible to others until their parent approves it. Parents can remove content at any time."],
      ["Service providers", "Cloud hosting & database, object storage for videos, AI moderation, and Firebase Cloud Messaging for parent notifications. Each is bound to use data only to provide their service."],
      ["Retention & deletion", "Parents can delete a child's profile or the whole family account in the app (Parent zone → Account). Deletion removes profiles, comments, activity and notifications and hides all videos immediately; backups are purged within 30 days."],
      ["Parents' rights (COPPA / GDPR-K)", `Parents can review, change or delete their child's information and withdraw consent at any time in the app or by emailing ${CONTACT}.`],
      ["Contact", CONTACT],
    ],
  },
  terms: {
    title: "Terms of Use",
    sections: [
      ["Accounts", "Only a parent or legal guardian aged 18+ may create an account. The parent is responsible for all child profiles under it."],
      ["Community rules", "Be kind. No bullying, scary, violent or adult content. Never share real names, schools, addresses, phone numbers or links. Videos are 10 seconds max."],
      ["Moderation", "Every video and comment is checked by AI and approved by a parent. Content reported by several users is hidden automatically pending review. We may remove content or suspend accounts that break these rules."],
      ["Your content", "You keep ownership of what you create. You give Zunii permission to store and show it to the audience your parent allowed."],
      ["Termination", "You can delete your account at any time from Parent zone → Account."],
      ["Contact", CONTACT],
    ],
  },
  safety: {
    title: "Child Safety Standards",
    sections: [
      ["Zero tolerance", "Zunii prohibits child sexual abuse and exploitation (CSAE) material and any grooming behaviour. Such content is removed and reported to the relevant authorities (e.g. NCMEC)."],
      ["Safety by design", "Parent-only signup, PIN-protected kid profiles, no private messaging, parent approval for all content, AI checks for text and video frames, screen-time limits, reporting & blocking for kids."],
      ["Reporting", `Kids can report any video, comment or creator with one tap. Parents and anyone else can contact our safety team at ${CONTACT}.`],
      ["Compliance", "We comply with applicable child-safety laws and cooperate with law enforcement."],
    ],
  },
};

export default function Legal() {
  const doc = DOCS[useLocation().pathname.slice(1)] || DOCS.privacy;
  return (
    <div className="min-h-screen bg-[#FFF7EC] pt-[env(safe-area-inset-top)]">
      <header className="max-w-3xl mx-auto px-4 sm:px-8 h-20 flex items-center justify-between">
        <Link to="/"><Logo className="text-2xl" /></Link>
        <Link to={-1} data-testid="legal-back-btn" className="zn-chip bg-white"><ArrowLeft size={16} /> Back</Link>
      </header>
      <main className="max-w-3xl mx-auto px-4 sm:px-8 pb-16">
        <h1 data-testid="legal-title" className="text-4xl sm:text-5xl font-bold mb-2">{doc.title}</h1>
        <p className="text-sm text-[#1B1A2E]/60 mb-8">{UPDATED}</p>
        <div className="zn-card p-6 sm:p-8 bg-white space-y-6">
          {doc.sections.map(([h, b]) => (
            <section key={h}><h2 className="text-xl font-semibold mb-1">{h}</h2><p className="text-[#1B1A2E]/80 leading-relaxed">{b}</p></section>
          ))}
        </div>
      </main>
    </div>
  );
}
